import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { useEffect, useRef, useState } from 'react';

export default function useEventDetails(activationId: string) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [eventName, setEventName] = useState('');
    const [eventDate, setEventDate] = useState<Date | null>(null);
    const [eventLocation, setEventLocation] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastChangesMade, setLastChangesMade] = useState<Date | null>(null);

    const fetchEventDetails = async () => {
        try {
            setIsLoading(true);
            const response = await authenticatedApi.get(
                `/${orgId}/activations/v2/${activationId}`,
            );
            const data = response.data;
            const details = data.eventDetails;
            setEventName(details.name);
            setEventDate(details.date ? new Date(details.date) : null);
            setEventLocation(details.location);
        } catch (error) {
            setError('Error fetching event details');
            console.error('Error fetching event details', error);
        } finally {
            setIsLoading(false);
        }
    };

    useUpdateEffect(() => {
        setLastChangesMade(new Date());
    }, [eventName, eventDate, eventLocation]);

    useEffect(() => {
        fetchEventDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const saveEventDetails = async () => {
        console.log('Saving event details');

        if (!eventName || !eventDate) {
            return;
        }

        setIsSaving(true);
        try {
            await authenticatedApi.put(
                `/${orgId}/activations/v2/${activationId}`,
                {
                    name: eventName,
                    eventDate: eventDate,
                    eventLocation: eventLocation,
                },
            );
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving event details', error);
            setError('Error saving event details');
        } finally {
            setIsSaving(false);
        }
    };

    useDebouncedEffect(
        saveEventDetails,
        [eventName, eventDate, eventLocation],
        2000,
    );

    return {
        eventName,
        eventDate,
        eventLocation,
        setEventName,
        setEventDate,
        setEventLocation,
        saveEventDetails,
        isSaving,
        lastSaved,
        isLoading,
        error,
        lastChangesMade,
    };
}

function useDebouncedEffect(fn: () => void, deps: any[], delay: number) {
    useEffect(() => {
        const handler = setTimeout(() => fn(), delay);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}

function useUpdateEffect(effect: () => void, deps: any[]) {
    const firstRenderRef = useRef(true);

    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        effect();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
